import React, {useState, useEffect} from 'react';
import {connect} from 'react-redux';
import {Box, FormControlLabel, Button,  InputBase, Paper, ClickAwayListener, InputAdornment} from '@mui/material';
import {makeStyles} from '@mui/styles'
import {Visibility, VisibilityOff} from '@mui/icons-material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {SMCCheckbox} from '../../components/Simple/SMCCheckbox';
import {getIsUsernameSaved, getSavedUsername, setIsUsernameSaved, setSavedUsername, removeSavedUsername, getSavedDomain, setSavedDomain, removeSavedDomain} from '../../utils/localStorage/localStorage';
import {useTranslation} from 'react-i18next';
import Api from '../../api/SMPFetchHelper';
import {setUserId, setCurrentPortal, setUserRole, setPortalTimeZone, setAvailablePortals, setAvailablePlans, setIsCyberViewsEnabled, setIsSMELogsEnabled} from '../../store/actions/user.actions';
import {isEmptyDomain, getErrorCode, getAxiosResponseErrorData} from '../../utils/utils';
import {SessionDataGetSchema} from '../../api/DBSchema/SessionData';
import {SMCPaths} from '../../conf/Routes';
import {ApiErrorCodes} from '../../api/consts';
import SMCLink from '../../components/Simple/SMCLink';
import {useBoolean, useToggle} from '../../utils/hooks';
import NewDomainRequestModal from '../PopupPages/NewDomainRequestModal';
import SMCLabel from '../../components/Simple/SMCLabel';
import {useSMCErrorHandler} from "../../utils/errorUtils";

/**
 * Logic section of login page
 * Input of domain-name, username, password and login button
 */
function LoginLogicSection({
    setUserId,
    setCurrentPortal,
    setUserRole,
    setPortalTimeZone,
    setAvailablePortals,
    setAvailablePlans,
    setIsCyberViewsEnabled,
    setIsSMELogsEnabled
}){
    const { t } = useTranslation();
    const classes = useStyles();

    const isUsernameSaved = getIsUsernameSaved();
    const { getErrorMessage } = useSMCErrorHandler();
    const defaultUsername = isUsernameSaved ? getSavedUsername() : '';
    const defaultDomain = getSavedDomain() || '';

    const [showPassword, toggleShowPassword] = useToggle(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [domainName, setDomainName] = useState(defaultDomain);
    const [username, setUsername] = useState(defaultUsername);
    const [saveUsername, setSaveUsername] = useState(isUsernameSaved);
    const [password, setPassword] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [renewPassword, setRenewPassword] = useState('');
    const [renewPassword2, setRenewPassword2] = useState('');
    const [showRenewPassword, toggleShowRenewPassword] = useToggle(false);
    const [showRenewPassword2, toggleShowRenewPassword2] = useToggle(false);


    // Domain autocomplete
    const [isDomainAutocompleteOpen, setIsDomainAutocompleteOpen] = useState(false);

    const [isTwoFactorAuthStep, setIsTwoFactorAuthStep] = useState(false);
    const [isPasswordRenewRequired, setIsPasswordRenewRequired] = useState(false);

    const [isNewDomainWindowOpen, openNewDomainWindow, closeNewDomainWindow] = useBoolean(false);

    // Clear error code and verification code on change of login step
    useEffect(() => {
        setErrorMessage('');
        setVerificationCode('');
    }, [isTwoFactorAuthStep, isPasswordRenewRequired]);

    /* Login logic */
    const doLogin = async () => {
        setIsSubmitting(true);
        try {
            // If "save username" checkbox is checked, save username to localStorage
            if (saveUsername) {
                setIsUsernameSaved(true);
                setSavedUsername(username);
            } else {
                setIsUsernameSaved(false);
                removeSavedUsername();
            }
        } catch (err) {
            console.error('Error saving username ' + err); // Not fatal, only print error log
        }
        try {
            const loginResp = await Api.login(domainName, username, password);
            const loginRespData = loginResp.data;
            if (!loginRespData) {
                throw new Error(ApiErrorCodes.GENERAL_ERROR);
            }
            if (loginRespData.isTwoFactorAuthRequired) {
                setIsTwoFactorAuthStep(true);
                setIsPasswordRenewRequired(loginRespData.isPasswordRenewRequired);
            } else if (loginRespData.isPasswordRenewRequired) {
                setIsPasswordRenewRequired(true);
            }
             else {
                /* Get session data */
                const sessionDataResp = await Api.getSessionData({attributes: [
                    SessionDataGetSchema.userId,
                    SessionDataGetSchema.portalName,
                    SessionDataGetSchema.userRole,
                    SessionDataGetSchema.serviceCenterTimeZone,
                    SessionDataGetSchema.availablePortals,
                    SessionDataGetSchema.availablePlans,
                    SessionDataGetSchema.isCyberViewsEnabled,
                    SessionDataGetSchema.isSMELogsEnabled
                ]});

                if (!sessionDataResp.data) {
                    throw new Error(ApiErrorCodes.GENERAL_ERROR);
                }
                const sessionData = sessionDataResp.data;
                setUserId(sessionData[SessionDataGetSchema.userId]);
                setCurrentPortal(
                    sessionData[SessionDataGetSchema.portalName] ?
                    sessionData[SessionDataGetSchema.portalName].toLowerCase() :
                    sessionData[SessionDataGetSchema.portalName]
                );
                setUserRole(sessionData[SessionDataGetSchema.userRole]);
                setPortalTimeZone(sessionData[SessionDataGetSchema.serviceCenterTimeZone]);
                setAvailablePortals(sessionData[SessionDataGetSchema.availablePortals]);
                setAvailablePlans(sessionData[SessionDataGetSchema.availablePlans]);
                setIsCyberViewsEnabled(sessionData[SessionDataGetSchema.isCyberViewsEnabled]);
                setIsSMELogsEnabled(sessionData[SessionDataGetSchema.isSMELogsEnabled]);
                /* Finish login */
                if (isEmptyDomain(sessionData[SessionDataGetSchema.portalName])) {
                    removeSavedDomain();
                    // Use window.location.href so that the application will refresh on each login
                    window.location.href = SMCPaths.systemSettings;
                } else {
                    // Remeber domain name for next login
                    setSavedDomain(sessionData[SessionDataGetSchema.portalName]);
                    // Use window.location.href so that the application will refresh on each login
                    window.location.href = SMCPaths.home; // Home only exists on non-empty domain (meanwhile...)
                }
            }
        } catch (err) {
            // Try to logout to remove the login token cookie
            Api.logout();
            const errorCode = getErrorCode(err);
            if (errorCode) {
                if (errorCode === ApiErrorCodes.USER_LOCKED) {
                    const errorData = getAxiosResponseErrorData(err);
                    setErrorMessage(t('com.sofaware.db.DB_LOGIN_IP_LOCKED', [errorData?.lockedSeconds]));
                } else {
                    setErrorMessage(t(errorCode));
                }
            } else {
                console.error(err);
                setErrorMessage(t(ApiErrorCodes.GENERAL_ERROR));
            }
        }
        setIsSubmitting(false);
    }

    // Two factor auth logic
    const doTwoFactorAuthLogin = async () => {
        if (!verificationCode) {
            setErrorMessage(t('SMC_LOGIN_INCORRECT_TWO_FA_CODE'));
            return;
        }
        setIsSubmitting(true);
        try {
            const confirmTwoFAResp = await Api.confirmTwoFA(verificationCode);
            const confirmTwoFARespData = confirmTwoFAResp.data.data;
            if (confirmTwoFARespData.isUserPassed2FAStep) {
                setIsTwoFactorAuthStep(false);
                if (!isPasswordRenewRequired) {
                    /* Get session data */
                    const sessionDataResp = await Api.getSessionData({attributes: [
                        SessionDataGetSchema.userId,
                        SessionDataGetSchema.portalName,
                        SessionDataGetSchema.userRole,
                        SessionDataGetSchema.serviceCenterTimeZone,
                        SessionDataGetSchema.availablePortals,
                        SessionDataGetSchema.availablePlans,
                        SessionDataGetSchema.isCyberViewsEnabled,
                        SessionDataGetSchema.isSMELogsEnabled
                    ]});

                    const sessionData = sessionDataResp.data;
                    setUserId(sessionData[SessionDataGetSchema.userId]);
                    setCurrentPortal(
                        sessionData[SessionDataGetSchema.portalName] ?
                        sessionData[SessionDataGetSchema.portalName].toLowerCase() :
                        sessionData[SessionDataGetSchema.portalName]
                    );
                    setUserRole(sessionData[SessionDataGetSchema.userRole]);
                    setPortalTimeZone(sessionData[SessionDataGetSchema.serviceCenterTimeZone]);
                    setAvailablePortals(sessionData[SessionDataGetSchema.availablePortals]);
                    setAvailablePlans(sessionData[SessionDataGetSchema.availablePlans]);
                    setIsCyberViewsEnabled(sessionData[SessionDataGetSchema.isCyberViewsEnabled]);
                    setIsSMELogsEnabled(sessionData[SessionDataGetSchema.isSMELogsEnabled]);
                    /* Finish login */
                    if (isEmptyDomain(sessionData[SessionDataGetSchema.portalName])) {
                        removeSavedDomain();
                        // Use window.location.href so that the application will refresh on each login
                        window.location.href = SMCPaths.systemSettings;
                    } else {
                        // Remeber domain name for next login
                        setSavedDomain(sessionData[SessionDataGetSchema.portalName]);
                        // Use window.location.href so that the application will refresh on each login
                        window.location.href = SMCPaths.home; // Home only exists on non-empty domain (meanwhile...)
                    }
                }
            }
            else {
                setErrorMessage(t('SMC_LOGIN_INCORRECT_TWO_FA_CODE'));
            }
        } catch (err) {
            const errorCode = getErrorCode(err);
            if (errorCode) {
                setErrorMessage(t(errorCode));
            } else {
                console.error(err);
                setErrorMessage(t(ApiErrorCodes.GENERAL_ERROR));
            }
        }
        setIsSubmitting(false);
    }


    // RenewPassword logic
    const doRenewPasswordLogin = async () => {
        if (!renewPassword || !renewPassword2 || renewPassword!==renewPassword2) {
            setErrorMessage(t("com.sofaware.db.CHANGEPASS_EMPTY"));
            return;
        }
        setIsSubmitting(true);
        try {
            const renewPasswordResponse = await Api.renewUserPassword(renewPassword, renewPassword2);
            const renewPasswordResponseData = renewPasswordResponse.data.data;

            if (renewPasswordResponseData.successRenewingPass) {
                /* Get session data */
                const sessionDataResp = await Api.getSessionData({attributes: [
                        SessionDataGetSchema.userId,
                        SessionDataGetSchema.portalName,
                        SessionDataGetSchema.userRole,
                        SessionDataGetSchema.availablePortals,
                        SessionDataGetSchema.availablePlans,
                        SessionDataGetSchema.isCyberViewsEnabled,
                        SessionDataGetSchema.isSMELogsEnabled
                    ]});

                const sessionData = sessionDataResp.data;
                setUserId(sessionData[SessionDataGetSchema.userId]);
                setCurrentPortal(
                    sessionData[SessionDataGetSchema.portalName] ?
                    sessionData[SessionDataGetSchema.portalName].toLowerCase() :
                    sessionData[SessionDataGetSchema.portalName]
                );
                setUserRole(sessionData[SessionDataGetSchema.userRole]);
                setAvailablePortals(sessionData[SessionDataGetSchema.availablePortals]);
                setAvailablePlans(sessionData[SessionDataGetSchema.availablePlans]);
                setIsCyberViewsEnabled(sessionData[SessionDataGetSchema.isCyberViewsEnabled]);
                setIsSMELogsEnabled(sessionData[SessionDataGetSchema.isSMELogsEnabled]);
                /* Finish login */
                if (isEmptyDomain(sessionData[SessionDataGetSchema.portalName])) {
                    removeSavedDomain();
                    // Use window.location.href so that the application will refresh on each login
                    window.location.href = SMCPaths.systemSettings;
                } else {
                    // Remeber domain name for next login
                    setSavedDomain(sessionData[SessionDataGetSchema.portalName]);
                    // Use window.location.href so that the application will refresh on each login
                    window.location.href = SMCPaths.home; // Home only exists on non-empty domain (meanwhile...)
                }
            }
            else {
                setErrorMessage(t(renewPasswordResponseData.errorMessage || ApiErrorCodes.GENERAL_ERROR));
            }
        } catch (err) {
            const errorCode = getErrorCode(err);
            if (errorCode) {
                if (errorCode === ApiErrorCodes.GENERAL_ERROR) {
                    //setErrorMessage(getErrorMessage(err));
                    alert(getErrorMessage(err))
                } else {
                    setErrorMessage(t(errorCode));
                }
            } else {
                console.error(err);
                setErrorMessage(t(ApiErrorCodes.GENERAL_ERROR));
            }
        }
        setIsSubmitting(false);
    }

    const loginStep = (
        <>
        <Box style={{ width: 350, left: 10, top: 50, position: 'relative' }} component="form">
            <Box className="service-domain-input" paddingBottom="10px">
                <ClickAwayListener onClickAway={() => setIsDomainAutocompleteOpen(false)}>
                    <Paper className={classes.inputRoot}>
                        <Box className="domain-icon" width={25}>
                            <FontAwesomeIcon icon="home" color="#000000de" style={{ fontSize: 14 }} />
                        </Box>
                        <InputBase
                            onKeyPress={e => e.which === 13 && doLogin()}
                            onKeyDown={e => e.key === 'Escape' && setIsDomainAutocompleteOpen(false)}
                            onClick={() => setIsDomainAutocompleteOpen(true)}
                            placeholder={t('EXT_LOGIN.DOMAIN')}
                            classes={{ input: classes.loginInput }}
                            name="servicedomain"
                            autoComplete="service-domain"
                            value={domainName}
                            onChange={e => setDomainName(e.target.value)}
                        />
                    </Paper>
                </ClickAwayListener>
                <Box className="domain-is-open" position="relative" display={isDomainAutocompleteOpen ? 'block' : 'none'}>
                </Box>
            </Box>
            <Box className="username-input" paddingBottom="10px">
                <Paper className={classes.inputRoot}>
                    <Box className={"user-icon"} width={25} paddingLeft="3px">
                        <FontAwesomeIcon icon="user" color="#000000de" style={{ fontSize: 13 }} />
                    </Box>
                    <InputBase
                        onKeyPress={e => e.which === 13 && doLogin()}
                        placeholder={t('INIT.LOGIN.USER_NAME')}
                        classes={{ input: classes.loginInput }}
                        name="username"
                        autoComplete="username"
                        value={username}
                        onChange={e => setUsername(e.target.value)}
                    />
                </Paper>
            </Box>
            <Box className="password-input" paddingBottom="10px">
                <Paper className={classes.inputRoot}>
                    <Box className="password-icon" width={25} paddingLeft="3px">
                        <FontAwesomeIcon icon="lock" color="#000000de" style={{ fontSize: 13 }} />
                    </Box>
                    <InputBase
                        onKeyPress={e => e.which === 13 && doLogin()}
                        placeholder={t('INIT.LOGIN.PASSWORD')}
                        type={showPassword ? 'text' : 'password'}
                        classes={{ input: classes.loginInput }}
                        name="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        endAdornment={
                            <div>
                                <InputAdornment
                                    position="end"
                                    onClick={toggleShowPassword}
                                    className={classes.passwordToggleEyeRoot}
                                >
                                    {
                                        showPassword ? (
                                            <VisibilityOff className={classes.passwordToggleEye} />
                                        ) : (
                                            <Visibility className={classes.passwordToggleEye} />
                                        )
                                    }
                                </InputAdornment>
                            </div>
                        }
                    />
                </Paper>
            </Box>
            <Box className="save-username" textAlign="left">
                <FormControlLabel
                    style={{ margin: 0 }}
                    label={t('INIT.LOGIN.SAVE_USER_NAME')}
                    labelPlacement="end"
                    control={<SMCCheckbox checked={saveUsername} onChange={e => setSaveUsername(e.target.checked)} />}
                />
            </Box>
            <Box className="error-message" textAlign="left">
                <span style={{ color: '#f86060', fontSize: '13px' }}>{errorMessage}</span>
            </Box>
            <Box className="new-domain-request" textAlign="left" marginTop="20px">
                <SMCLink onClick={openNewDomainWindow} style={{ fontSize: '13px' }}>{t('EXT_LOGIN.NEW_DOMAIN_REQUEST')}</SMCLink>
            </Box>
            {/* Loading spinner, if you can find a better one, change this one */}
            <Box className="loading-spinner" position="absolute" top="230px" left="210px">
                <div className="lds-dual-ring" style={{ visibility: isSubmitting ? 'visible' : 'hidden' }} />
            </Box>
            {/* Submit button */}
            <Box className="submit" position="absolute" top="230px" left="250px">
                <Button disabled={isSubmitting} onClick={doLogin} classes={{root: classes.loginButton}}>{t('INIT.BUTTON.LOGIN')}</Button>
            </Box>
        </Box>
        {/* Popup windows */}
        <NewDomainRequestModal
            isOpen={isNewDomainWindowOpen}
            onRequestClose={closeNewDomainWindow}
        />
        </>
    );


    const twoFactorAuthStep = (
        <>
        <Box style={{ width: 350, left: 10, top: 10, position: 'relative' }}>
            <SMCLabel style={{ fontSize: '13px', marginBottom: '16px' }}>{t('EXT_LOGIN.ENTER_2FA')}</SMCLabel>
            {/* Service domain input */}
            <Box paddingBottom="10px">
                <Paper className={classes.inputRoot}>
                    <Box width={25} paddingLeft="3px">
                        <FontAwesomeIcon icon="lock" color="#000000de" style={{ fontSize: 13 }} />
                    </Box>
                    <InputBase
                        onKeyPress={e => e.which === 13 && doTwoFactorAuthLogin()}
                        placeholder={t('EXT_LOGIN.VERIFICATION_CODE')}
                        classes={{ input: classes.loginInput }}
                        name="verificationCode"
                        value={verificationCode}
                        onChange={e => setVerificationCode(e.target.value)}
                    />
                </Paper>
            </Box>
            <Box textAlign="left">
                <span style={{ color: '#f86060', fontSize: '13px' }}>{errorMessage}</span>
            </Box>
            {/* Loading spinner, if you can find a better one, change this one */}
            <Box position="absolute" top="270px" left="100px">
                <div className="lds-dual-ring" style={{ visibility: isSubmitting ? 'visible' : 'hidden' }} />
            </Box>
            {/* Cancel button */}
            <Box position="absolute" top="270px" left="130px">
                <Button
                    disabled={isSubmitting}
                    onClick={() => {
                        setIsTwoFactorAuthStep(false);
                        setIsPasswordRenewRequired(false);
                    }}
                    classes={{root: classes.cancelButton}}>{t('SMC_GEN_CANCEL')}</Button>
            </Box>
            {/* Submit button */}
            <Box position="absolute" top="270px" left="250px">
                <Button disabled={isSubmitting} onClick={doTwoFactorAuthLogin} classes={{root: classes.loginButton}}>{t('SMC_GEN_APPLY')}</Button>
            </Box>
        </Box>
        </>
    );

    const renewPasswordStep = (
        <>
            <Box style={{ width: 350, left: 10, top: 10, position: 'relative' }}>
                <SMCLabel style={{ fontSize: '13px', marginBottom: '16px' }} >{t("EXT_LOGIN.SMP.RENEW_PASSWORD.ENTER")}</SMCLabel>
                <Box paddingBottom="10px" paddingTop="10px">
                    <Paper className={classes.inputRoot}>
                        <Box width={25} paddingLeft="3px">
                            <FontAwesomeIcon icon="lock" color="#000000de" style={{ fontSize: 13 }} />
                        </Box>
                        <InputBase
                            onKeyPress={e => e.which === 13 && renewPassword2.focus()}
                            placeholder={t('EXT_LOGIN.SMP.RENEW_PASSWORD.TEXTBOX1')}
                            classes={{ input: classes.loginInput }}
                            required={true}
                            name="renewPassword"
                            type={showRenewPassword ? 'text' : 'password'}
                            value={renewPassword}
                            onChange={e => setRenewPassword(e.target.value)}
                            endAdornment={
                                <div>
                                    <InputAdornment
                                        position="end"
                                        onClick={toggleShowRenewPassword}
                                        className={classes.passwordToggleEyeRoot}
                                    >
                                    {
                                        showRenewPassword ? (
                                            <VisibilityOff className={classes.passwordToggleEye} />
                                        ) : (
                                            <Visibility className={classes.passwordToggleEye} />
                                        )
                                    }
                                    </InputAdornment>
                                </div>
                            }
                        />
                    </Paper>
                </Box>
                <Box paddingBottom="10px">
                    <Paper className={classes.inputRoot}>
                        <Box width={25} paddingLeft="3px">
                            <FontAwesomeIcon icon="lock" color="#000000de" style={{ fontSize: 13 }} />
                        </Box>
                        <InputBase
                            onKeyPress={e => e.which === 13 && doRenewPasswordLogin()}
                            placeholder={t('EXT_LOGIN.SMP.RENEW_PASSWORD.TEXTBOX2')}
                            classes={{ input: classes.loginInput }}
                            required={true}
                            name="renewPassword2"
                            type={showRenewPassword2 ? 'text' : 'password'}
                            value={renewPassword2}
                            onChange={e => setRenewPassword2(e.target.value)}
                            endAdornment={
                                <div>
                                    <InputAdornment
                                        position="end"
                                        onClick={toggleShowRenewPassword2}
                                        className={classes.passwordToggleEyeRoot}
                                    >
                                    {
                                        showRenewPassword2 ? (
                                            <VisibilityOff className={classes.passwordToggleEye} />
                                        ) : (
                                            <Visibility className={classes.passwordToggleEye} />
                                        )
                                    }
                                    </InputAdornment>
                                </div>
                            }
                        />
                    </Paper>
                </Box>
                <Box textAlign="left" position="relative">
                    <div style={{ color: '#f86060', fontSize: '13px', display: 'inline-block', whiteSpace: 'pre-wrap' }}>
                        <innerText>{errorMessage}</innerText>
                    </div>
                </Box>
                {/* Loading spinner, if you can find a better one, change this one */}
                <Box position="absolute" top="270px" left="100px">
                    <div className="lds-dual-ring" style={{ visibility: isSubmitting ? 'visible' : 'hidden' }} />
                </Box>
                {/* Cancel button */}
                <Box position="absolute" top="270px" left="130px">
                    <Button disabled={isSubmitting}
                            onClick={() => {
                                setIsTwoFactorAuthStep(false);
                                setIsPasswordRenewRequired(false);
                            }}
                            classes={{root: classes.cancelButton}}>{t('SMC_GEN_CANCEL')}</Button>
                </Box>
                {/* Submit button */}
                <Box position="absolute" top="270px" left="250px">
                    <Button
                        disabled={isSubmitting || (renewPassword !== renewPassword2) || !renewPassword}
                        onClick={doRenewPasswordLogin}
                        classes={{root: classes.loginButton}}>{t('SMC_GEN_APPLY')}</Button>
                </Box>
            </Box>
        </>
    );

    if (isTwoFactorAuthStep) {
        return twoFactorAuthStep;
    }
    else {
        if (isPasswordRenewRequired) {
            return renewPasswordStep;
        }
        else {
            return loginStep;
        }
    }
}

const useStyles = makeStyles({
    loginButton: {
        backgroundColor: '#56b40a !important',
        height: 30,
        width: 110,
        borderRadius: '0 !important',
        color: '#fff !important',
        padding: '0 !important',
        fontSize: '0.875rem !important'
    },
    cancelButton: {
        backgroundColor: '#A7A9AC',
        height: 30,
        width: 110,
        borderRadius: 0,
        color: '#fff',
        padding: 0,
        fontSize: '0.875rem'
    },
    inputRoot: {
        padding: '2px 4px',
        display: 'flex',
        borderRadius: 0,
        alignItems: 'center',
        boxShadow: 'none',
        width: '100%',
        height: 34,
        boxSizing: 'border-box',
        border: '1px solid transparent',
        '&:focus-within': {
            border: '1px solid #4fb4f1'
        }
    },
    loginInput: {
        paddingLeft: 5,
        border: 'none !important',
        paddingBottom: 5,
        paddingTop: 5,
        width: '280px !important',
        height: '30px !important',
        fontSize: '15px',
        '&:-webkit-autofill': {
            transitionDelay: '9999s',
            transitionProperty: 'background-color, color'
        },
        '&::placeholder' : {
            color: '#0a0a0a !important',
            fontStyle: 'normal !important',
            fontSize: '15px !important'
        }
    },
    passwordToggleEyeRoot: {
        position: 'absolute',
        marginLeft: '-30px',
        cursor: 'default'
    },
    passwordToggleEye: {
        width: '0.95em',
        height: '0.95em',
        opacity: '0.7'
    }
});

const mapStateToProps = null;

const mapDispatchToProps = {
    setUserId,
    setCurrentPortal,
    setUserRole,
    setPortalTimeZone,
    setAvailablePortals,
    setAvailablePlans,
    setIsCyberViewsEnabled,
    setIsSMELogsEnabled
}

export default connect(mapStateToProps, mapDispatchToProps)(LoginLogicSection);
